/* eslint-disable react/prop-types */
import {lazy, Suspense, useCallback, useContext, useEffect, useReducer, useRef, useState} from 'react'

import {useUserConsents} from '@s-ui/consents'
import cookie from '@s-ui/js/lib/cookie'
import {useDecision, useDecisionCallback, useExperiment} from '@s-ui/pde'
import Button, {atomButtonColors, atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_COLORS, ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomSpinner, {atomSpinnerOverlayTypes, atomSpinnerTypes} from '@s-ui/react-atom-spinner'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount, useNearScreen} from '@s-ui/react-hooks'
import IconError from '@s-ui/react-icons/lib/Lineerror'
import MoleculeNotification, {
  moleculeNotificationAutoClose,
  moleculeNotificationPosition,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import MoleculeTabs, {MoleculeTab, moleculeTabsVariants} from '@s-ui/react-molecule-tabs'
import {useLocation, useNavigate} from 'react-router-dom'
import ScriptJsonLD from '@s-ui/react-script-json-ld'
import {FeedbackList} from '@adv-mt/frontend-ma--lib-trust'
import AdDisplay from '@adv-mt/mt-ad-display'
import AdvertisingTextlinks from '@adv-mt/mt-advertising-textlinks'
import BannerInfo from '@adv-mt/mt-banner-info'
import CardCarfax from '@adv-mt/mt-card-carfax'
import CardCertification from '@adv-mt/mt-card-certification'
import CardFinancingLendo from '@adv-mt/mt-card-financing-lendo'
import CardUser from '@adv-mt/mt-card-user'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import FeedArticles from '@adv-mt/mt-feed-articles'
import {formContactSellerFields as FORM_FIELDS} from '@adv-mt/mt-form-contact-seller'
import HeroDealer, {heroDealerDesigns} from '@adv-mt/mt-hero-dealer'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import StickyLeadContactButtons from '@adv-mt/mt-lead-contact-buttons'
import ListRelatedAds, {listRelatedAdsContactTypes, listRelatedAdsDisplay} from '@adv-mt/mt-list-related-ads'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import LoginGoogleOneTapToGigyaConnector from '@adv-mt/mt-login-google-one-tap-to-gigya-connector'
import NavigationAdToolbar from '@adv-mt/mt-navigation-ad-toolbar'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import NetDrawer, {useReopenDrawerOnSession} from '@adv-mt/mt-net-drawer'
import NewsletterSubscription from '@adv-mt/mt-newsletter-subscription'
import NotificationsAdFavorite from '@adv-mt/mt-notifications-ad-favorite'
import NotificationsDealerFavorite from '@adv-mt/mt-notifications-dealer-favorite'
import NotificationsVehicleReservation from '@adv-mt/mt-notifications-vehicle-reservation'
import PanelAdDetails from '@adv-mt/mt-panel-ad-details'
import PanelAdInfo from '@adv-mt/mt-panel-ad-info'
import PanelBasic from '@adv-mt/mt-panel-basic'
import PanelEquipment, {panelEquipmentCTADesigns, panelEquipmentCTATypes} from '@adv-mt/mt-panel-equipment'
import PanelSearchSave, {searchSaveTrackingOrigins} from '@adv-mt/mt-panel-search-save'
import PanelVehicleReservation from '@adv-mt/mt-panel-vehicle-reservation'
import SearchSave from '@adv-mt/mt-search-save'
import SectionBasic from '@adv-mt/mt-section-basic'
import SectionDetailInstantOffer from '@adv-mt/mt-section-detail-instant-offer'
import SectionFinancingTerms from '@adv-mt/mt-section-financing-legal-terms'
import SectionServices, {sectionServicesContext} from '@adv-mt/mt-section-services'
import SharedSmartbanner, {sharedSmartbannerPositions} from '@adv-mt/mt-shared-smartbanner'
import IconCircleCheck from '@adv-mt/mt-svg-icons/lib/CircleCheck'
import IconCircleClose from '@adv-mt/mt-svg-icons/lib/CircleClose'
import IconWhatsApp from '@adv-mt/mt-svg-icons/lib/Whatsapp'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackPageEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'
import {useCsat} from '@adv-mt/mt-tracking-use-csat'
import {createToastManager, TOAST_TYPES, ToastProvider, ToastViewport} from '@adv-mt/ui/toast'

import LinkFactory from '../../../../components/LinkFactory/index.js'
import {ROUTES} from '../../../../config/index.js'
import CsatNpsTracker from '../../../../helpers/csatNps/CsatNpsTracker.js'
import {adaptDomainNewsFeed} from '../../../News/List/adapters/adaptDomainNewsFeed.js'
import {reducer, reducerActions, reducerInitialState} from '../../reducer.js'
import PanelPriceIndicator from './experiments/PanelPriceIndicator/index.js'
// TODO: Move this modal to context Modal when the experiment has ended --> https://jira.ets.mpi-internal.com/browse/MTR-79791
import ModalDealerRatings from './ModalDealerRatings/index.js'
import DetailFormContactSellerWrapper from './DetailFormContactSellerWrapper.js'
import DetailPanelFinancingLazy from './DetailPanelFinancingLazy.js'
import Gallery from './galleryWrapper.js'
import {FF_WHATSAPP_LEAD_EXPERIENCE, TARGET_BUYER_TAB_INDEX} from './settings.js'
import {
  getFinancingData,
  getHasPhoneCallLabelVariant,
  getIsWhatsappLoginRequiredVariant,
  getLinkFactoryWithState
} from './utils.js'

import './styles/index.scss'

const scrollParams = {behavior: 'smooth', block: 'center'}

const FINANCING_TERMS_CONDITIONS_ID = 'mt-Detail-financingTerms'
const TRANSACTION_C2C_PANEL_ID = 'mt-Detail-transactionC2cPanel'
const TRANSACTION_C2C_PANEL_CALCULATOR_ID = 'calculator'
const TRANSACTION_C2C_PANEL_CALCULATOR_HASH = `#${TRANSACTION_C2C_PANEL_CALCULATOR_ID}`

const LEGACY_NEWS_ITEMS_COUNT = 2

const ModalBasic = lazy(() => import(/* webpackChunkName: "ModalBasic" */ '@adv-mt/mt-modal-basic'))

const CustomChildren = ({loader}) => {
  const {i18n} = useContext(Context)

  return (
    <div className="mt-Detail-whatsappSpinner">
      {loader}
      <span>{i18n.t('WHATSAPP_LEAD_BUTTON.SPINNER_TEXT')}</span>
    </div>
  )
}

const TrackingListRelatedAds = ({ads, listType, onProductsReceived}) => {
  const {domain} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()
  useMount(() => {
    domain
      .get('get_advertisements_tracking_use_case')
      .execute({
        ads: ads.map(({id, title, make, model, location, pack}) => ({
          id,
          title,
          make,
          model,
          location,
          pack
        })),
        adsTypeName: 'ad_impression',
        listType
      })
      .then(({products}) => {
        analyticsTrack({
          event: 'Ad Impressions Collected',
          data: {
            products
          }
        })
        onProductsReceived?.({products})
      })
  })

  return <></>
}

const Detail = ({
  ad = {},
  adRentingForBusiness = {},
  adType = {},
  alertSearch = {},
  breadcrumb = [],
  createdAlert,
  currentUser,
  dealerAds,
  adFavoriteInfo,
  dealerFavoriteInfo,
  extendedTrackingData = {},
  financingData,
  helmet = {},
  isRecommendedAdsFallbackValue,
  isPreview,
  isTradableAd,
  leadData = {},
  multiProvinceTrackingData,
  newsFeed,
  origin = '',
  pageErrors = {},
  pageName = '',
  pageTrackingData = {},
  relatedAds,
  reservationNewFlow = {},
  seller = {
    dealer: {}
  },
  showB2cFinancingPanel = false,
  showC2cFinancing = false,
  showPriceIndicator,
  tagclouds = [],
  targetBuyer,
  vehicleCertification = {},
  vehicleHistory,
  vehicleInfo = {},
  videoPlay,
  videoTime,
  __HTTP__ = {}
}) => {
  // Even though this is placed at the top of the page,
  // When we do Client-side rendering the component is rendered
  const {redirectTo} = __HTTP__

  const navigate = useNavigate()
  const [isNear, outerRef] = useNearScreen()
  const {domain, userAgent, i18n} = useContext(Context)
  const {decide} = useDecisionCallback()
  const isSafari = /^(?!.*(?:Chrome|CriOS|Chromium|Edg|OPR)).*Safari/i.test(userAgent)
  const {track: analyticsTrack, page: analyticsTrackPage} = useAnalyticsTrack()
  const {track: criteoTrack} = useCriteoTrack()
  const {trigger} = useCsat()
  const {isLogged} = useContext(AuthenticationContext)
  const {hash, pathname, search: queryString} = useLocation()
  const {AD_DETAIL_RESERVATION_HASHES: RESERVATION_HASHES} = domain.get('config').get('page')

  const {triggerLoginModal} = useContext(LoginModalContext)
  const contactFormWrapperRef = useRef()
  const panelAdInfoRef = useRef()
  const panelPriceIndicatorRef = useRef()
  const panelInfoDealerRef = useRef()
  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm)
  const isDesktop = useMediaQuery(useMediaQueryBreakpoints.lg, {defaultMatches: true})
  const [isOpenModalDealerRating, setIsOpenModalDealerRating] = useState(false)
  const [isShowContactFormModal, setIsShowContactFormModal] = useState(false)
  const [isWhatsappLoading, setIsWhatsappLoading] = useState(false)
  const [isWhatsappError, setIsWhatsappError] = useState(false)
  const [showReservationWrongUserNotification, setShowReservationWrongUserNotification] = useState(
    hash === RESERVATION_HASHES.WRONG_USER
  )
  const [showReservationNotAvailableNotification, setShowReservationNotAvailableNotification] = useState(
    hash === RESERVATION_HASHES.NOT_AVAILABLE
  )
  const [createdAlertState, setCreatedAlertState] = useState(createdAlert)

  const [dealerAdsProducts, setDealerAdsProducts] = useState(null)
  const [relatedAdsProducts, setRelatedAdsProducts] = useState(null)
  const [ratingsList, setRatingsList] = useState([])
  const [isRatingsLoading, setIsRatingsLoading] = useState(true)

  const [isNetOpen, setIsNetOpen] = useState(false)
  const [netSearchValue, setNetSearchValue] = useState(null)
  const [netInitialSuggestions, setNetInitialSuggestions] = useState(null)

  const isSubscriptionAd = Boolean(ad.monthlySubscriptionFee)

  const config = domain.get('config')
  const isCoches = config.get('APP_NAME') === config.get('APP_NAME_COCHES')

  const {DRAWER_REOPEN_SESSION_KEY, NET_SUGGESTION_TYPE} = config.get('COMMON').AI_ASSISTANT
  const {
    SERVICE_PROVIDERS: {CARFAX, LENDO},
    COOKIES: {VISITED_AD: visitedAdCookieKey},
    RECOMMENDED_ADS: {
      adsList: {visitedAdSessionStorageKey}
    }
  } = config.get('COMMON')
  const {AVAILABLE_SERVICE_PROVIDERS} = config.get('serviceProviders')
  const {RESERVATION_STATUSES} = config.get('financing')
  const {
    TRACKING_EVENT_NAMES,
    HIT_INFORMATIONS: {SMARTBANNER_POSITION_TOP: hitInformationSmartbannerPositionTop},
    HIT_ORIGINS: {
      SMARTBANNER_AD_DETAIL: hitOriginSmartbannerAdDetail,
      LEAD_WHATSAPP: hitOriginLeadWhatsapp,
      RESERVATION: hitOriginReservation
    }
  } = config.get('tracking')

  const {
    DEFAULT_CATEGORY1_ID,
    OFFER_TYPE_KM0,
    OFFER_TYPE_NEW,
    OFFER_TYPE_SECOND_HAND,
    OFFER_TYPE_SUBSCRIPTION,
    CARS_WITHOUT_LICENSE_CATEGORY1_ID
  } = config.get('search')

  const {NEWSLETTER_BANNER_ENABLED} = config.get('editorial')

  const isLendoAvailable = Boolean(AVAILABLE_SERVICE_PROVIDERS[LENDO])
  const hasVehicleHistory = Boolean(vehicleHistory)

  const showFinancingLendoCard = isLendoAvailable && !isSubscriptionAd

  const hasFinancing = seller?.dealer && Boolean(financingData?.id)

  const {category1Id, offerTypeId} = adType

  const subscriptionPriceOptionSelected =
    ad.subscriptionPriceOptions?.find(({monthlyPrice}) => monthlyPrice === ad.monthlySubscriptionFee) ||
    ad.subscriptionPriceOptions?.[0]

  const [state, dispatch] = useReducer(reducer, {
    ...reducerInitialState,
    leadData,
    userData: currentUser,
    targetBuyer: subscriptionPriceOptionSelected?.targetBuyer ?? targetBuyer,
    selectedTabAdInfo: TARGET_BUYER_TAB_INDEX[targetBuyer],
    isFavorite: Boolean(adFavoriteInfo),
    subscriptionPriceOptionSelected
  })

  const isCanarias = config.get('CANARIAS_PROVINCE_IDS').includes(ad.provinceId)
  const showCanariasBannerInfo = isCanarias && seller?.isDealer

  const showPanelVehicleReservation =
    seller?.isDealer &&
    seller?.dealer?.hasReservation &&
    reservationNewFlow.adReservationStatus !== RESERVATION_STATUSES.NON_RESERVABLE

  const hasRelatedAds = Boolean(relatedAds?.length)

  const showReservationMainCTA =
    seller?.isDealer &&
    seller?.dealer?.hasReservation &&
    reservationNewFlow.adReservationStatus === RESERVATION_STATUSES.RESERVABLE

  const isNewVehicle = offerTypeId === OFFER_TYPE_NEW
  const isRenting = offerTypeId === OFFER_TYPE_SUBSCRIPTION

  // ff_web_lead_experience_whatsapp_label_buttons — ABC: `a` exige login para contactar por WhatsApp,
  // `c` mueve la etiqueta del CTA de WhatsApp al de llamada. Se resuelve aquí, en el render, porque la
  // etiqueta ya se pinta en SSR; esta misma llamada registra la exposición en cliente (en servidor pde
  // nunca emite el evento). Es la única decisión del flag en la página: el gate de WhatsApp de la barra
  // sticky y el del formulario reutilizan `whatsappLeadExperienceVariant`.
  // `is_logged_in` alimenta la audiencia que deja el test solo para usuarios no logueados: para un
  // usuario logueado las variantes `a` y `b` son el mismo tratamiento y solo añadirían ruido.
  const {enabled: isWhatsappLeadExperienceEnabled, variationKey: whatsappLeadExperienceVariationKey} = useDecision(
    FF_WHATSAPP_LEAD_EXPERIENCE,
    {
      attributes: {page_name: pageName, isProfessionalSeller: seller?.isDealer, is_logged_in: isLogged},
      queryString
    }
  )
  const whatsappLeadExperienceVariant = isWhatsappLeadExperienceEnabled ? whatsappLeadExperienceVariationKey : null
  const hasPhoneCallLabel = getHasPhoneCallLabelVariant(whatsappLeadExperienceVariant)

  // MTR-117126: lead post-favoritos — coches-only, PRO-dealer, logged-in
  const isFavoriteLeadEnabled = Boolean(seller?.isDealer) && isLogged && isCoches
  const [favoriteLeadToastManager] = useState(() => createToastManager())

  const {variation} = useExperiment({
    experimentName: 'abtest8_web_newvehicle_call_logged_in',
    attributes: {page_name: pageName, isNewVehicle},
    queryString
  })

  const showSimplifiedFinancingPanel = hasFinancing && !showB2cFinancingPanel && !showC2cFinancing

  // ff_web_lead_experience_lead_enrichment_mobile_desktop
  const isVOorVNorKM0 =
    [OFFER_TYPE_SECOND_HAND, OFFER_TYPE_KM0, OFFER_TYPE_NEW].includes(offerTypeId) &&
    [DEFAULT_CATEGORY1_ID].includes(category1Id)

  const enableLeadEnrichment = isVOorVNorKM0 && !isDesktop

  const isLoggedUsersPhoneCall = variation === 'variation'

  const panelAdInfoWrapperClassName = 'mt-Detail-panelAdInfoWrapper'

  useEffect(() => {
    if (!seller?.isDealer || !seller?.contractId) return

    const {API_URL, TRUST_API_PATH} = {
      API_URL: config.get('API_URL'),
      TRUST_API_PATH: config.get('TRUST_API_PATH')
    }
    const {SDRN_TRUST_BASE} = config.get('ratings')

    const feedbackList = new FeedbackList({
      appName: config.get('APP_NAME'),
      baseUrl: `${API_URL}${TRUST_API_PATH}`,
      subjectId: `${SDRN_TRUST_BASE}${seller.contractId}`
    })

    feedbackList
      .getUserReviews()
      .then(reviews => {
        setRatingsList(reviews)
        setIsRatingsLoading(false)
      })
      .catch(error => {
        setIsRatingsLoading(false)
        console.error('Error fetching dealer reviews:', error) // eslint-disable-line
      })
  }, [seller?.contractId]) // eslint-disable-line

  useMount(() => {
    // We save the id on the session storage to show recommender when there are less than minResultsToShowRecommendations -> kept on the whole session
    sessionStorage.setItem(visitedAdSessionStorageKey, ad.id)
    // We save the id on the cookies to show recommender after visiting a specific ad -> this will be removed after showing it
    cookie.set(visitedAdCookieKey, ad.id)
  })

  // we must force the scrollToTop to avoid
  // that users coming from the parrilla after having scrolled through their search,
  // visualize the detail from the bottom of the page.
  useMount(() => {
    window.scrollTo(0, 0)
  })

  useReopenDrawerOnSession({
    isEnabled: isCoches,
    storageKey: DRAWER_REOPEN_SESSION_KEY,
    onToggleDrawer: newStatus => setIsNetOpen(newStatus)
  })

  const onOpenNet = searchValue => {
    const {suggestions, ...search} = searchValue || {}
    setNetSearchValue(search?.text ? search : null)
    setNetInitialSuggestions(suggestions || null)
    setIsNetOpen(true)
  }

  const onCloseNet = () => {
    setNetSearchValue('')
    setNetInitialSuggestions(null)
    setIsNetOpen(false)
  }

  const handleScrollToContactForm = ({event}) => {
    /* we still need to access the element via getElementById
    because using ref.current.scrollIntoView doesn't work properly
    when there are elements lazy loading above the form */
    const elementContactForm = document.getElementById('form-contact-seller-id')
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)

    dispatch({
      type: reducerActions.SET_FINANCING_TAB,
      payload: {numTab: 2}
    })

    analyticsTrack({
      event
    })
  }

  const handleScrollToContactSellerWithFinancing = () => {
    const elementContactSeller = document.getElementById('form-contact-seller-id')
    elementContactSeller && elementContactSeller.scrollIntoView(scrollParams)

    dispatch({
      type: reducerActions.SET_INITIAL_LEAD_ENRICHMENT,
      payload: {initialLeadEnrichmentValue: ['FINANCING']}
    })
  }

  const handleScrollToFinancingTerms = () => {
    const elementContactForm = document.getElementById(FINANCING_TERMS_CONDITIONS_ID)
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)
  }

  const createWhatsappIntent = () => domain.get('create_whatsapp_intents_user_use_case').execute({resourceId: ad.id})

  // Safari blocks window.open after await — open empty tab synchronously first, then navigate it
  const openWhatsappTab = () => (isSafari ? window.open('', '_blank') : null)
  const navigateToWhatsapp = (tab, chatDeepLink) => {
    if (tab) {
      tab.location.href = chatDeepLink
    } else {
      window.open(chatDeepLink, '_blank', 'noopener,noreferrer')
    }
  }

  const handleWhatsappLeadTrack = async ({id}) => {
    const {leadData = {}} = state
    await analyticsTrack({
      event: 'Lead Triggered',
      data: {
        lead_type: config.get('tracking').LEAD_TYPES.DETAIL_AD_REPLIED_WHATSAPP,
        ...leadData,
        hit_information3: id
      }
    })
  }

  const handleWhatsappClickTrack = async () => {
    await analyticsTrack({
      event: 'Whatsapp Button Clicked',
      data: {
        hit_origin: 'detail'
      }
    })
  }

  const handleStickyWhatsappDeepLink = async () => {
    if (!isSafari) setIsWhatsappLoading(true)
    const tab = openWhatsappTab()

    const [error, chatResponse] = await createWhatsappIntent()

    if (chatResponse?.chatDeepLink) {
      const {id} = chatResponse

      await handleWhatsappLeadTrack({id})

      if (isSafari) {
        navigateToWhatsapp(tab, chatResponse.chatDeepLink)
      } else {
        setTimeout(() => {
          setIsWhatsappLoading(false)
          navigateToWhatsapp(tab, chatResponse.chatDeepLink)
        }, 3000)
      }

      return
    }

    tab?.close()
    setIsWhatsappLoading(false)
    if (error) setIsWhatsappError(true)
  }

  const handleStickyWhatsappIntent = useCallback(async () => {
    // ff_lead_experience_login_whatsapp sigue siendo el kill-switch: encenderlo desactiva el login
    // en todas las variantes sin deploy
    const {enabled: isSkipLoginEnabled} = decide('ff_lead_experience_login_whatsapp')
    // ff_web_lead_experience_whatsapp_label_buttons — solo la variante `a` exige login
    const isLoginRequired =
      !isLogged && !isSkipLoginEnabled && getIsWhatsappLoginRequiredVariant(whatsappLeadExperienceVariant)

    await handleWhatsappClickTrack()

    if (isLoginRequired) {
      const {LOGIN_SOURCE_ID_WHATSAPP_LEAD} = config.get('login')
      triggerLoginModal({
        modalProps: {
          trackingOrigin: hitOriginLeadWhatsapp,
          source: LOGIN_SOURCE_ID_WHATSAPP_LEAD,
          onLogin: handleStickyWhatsappDeepLink
        }
      })
      return
    }

    handleStickyWhatsappDeepLink()
  }, [ad.id, isLogged, whatsappLeadExperienceVariant]) // eslint-disable-line

  const handleFavoriteLeadConfirm = async () => {
    const [contactError] = await domain.get('contact_seller_use_case').execute({
      adId: ad.id,
      contractId: seller?.contractId,
      context: pageName,
      name: state.userData?.name,
      email: state.userData?.email,
      phone: state.userData?.phone,
      zipCode: state.userData?.zipCode,
      message: i18n.t('LEAD_POST_FAVORITE.LEAD_MESSAGE'),
      hasAcceptedTermsAndConditions: true,
      // The buyer never types here — the copy is always ours. The form derives this from
      // `message === CONTACT_DEFAULT_MESSAGE`; the flag means "not written by the buyer",
      // not "equal to that exact string", so it is always true for a post-favorite lead.
      isDefaultMessage: true,
      isPostFavoriteLead: true,
      skipSaveContactDetails: true,
      // Same shape the contact form sends (ContactButtonsExperiment), so a subscription ad's
      // lead carries `preferences.subscription.commitment`. Omitting it drops the block entirely.
      subscriptionCommitment:
        isSubscriptionAd && state.subscriptionPriceOptionSelected
          ? {
              totalMonths: state.subscriptionPriceOptionSelected.totalMonths,
              monthlyPrice: state.subscriptionPriceOptionSelected.monthlyPrice,
              kmIncluded: state.subscriptionPriceOptionSelected.kmIncluded,
              kmIncludedPeriod: state.subscriptionPriceOptionSelected.kmIncludedPeriod,
              targetBuyer: state.targetBuyer
            }
          : undefined
    })

    if (contactError) {
      favoriteLeadToastManager.add({
        title: i18n.t('LEAD_POST_FAVORITE.TOAST.ERROR.TITLE'),
        description: i18n.t('LEAD_POST_FAVORITE.TOAST.ERROR.BODY'),
        type: TOAST_TYPES.DESTRUCTIVE,
        // IconError (Lineerror, used by MoleculeNotification above) doesn't support a `color`
        // prop — IconCircleClose is the AtomIcon-based equivalent, paired with IconCircleCheck below
        data: {icon: <IconCircleClose color={ATOM_ICON_COLORS.error} size={ATOM_ICON_SIZES.medium} />}
      })
      return
    }

    favoriteLeadToastManager.add({
      title: i18n.t('LEAD_POST_FAVORITE.TOAST.SUCCESS.TITLE'),
      description: i18n.t('LEAD_POST_FAVORITE.TOAST.SUCCESS.BODY'),
      type: TOAST_TYPES.SUCCESS,
      data: {icon: <IconCircleCheck color={ATOM_ICON_COLORS.success} size={ATOM_ICON_SIZES.medium} />}
    })

    // Same tracking payload the form and WhatsApp leads send (`state.leadData`, resolved in
    // getInitialProps with `pageType: 'detail'`), so `lead_type` is the only intended delta.
    // Re-running get_lead_data_tracking_use_case here produced a strict subset — no
    // page_name_origin, status_car, has_instalment, has_online_financing nor filters.
    analyticsTrack({
      event: 'Lead Triggered',
      data: {
        lead_type: config.get('tracking').LEAD_TYPES.DETAIL_FAVORITE_CHAT,
        ...state.leadData
      }
    })
  }

  const scrollToFinancingCalculatorPanel = () => {
    const elementContactForm = document.getElementById(TRANSACTION_C2C_PANEL_ID)
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)
  }

  if (hash === TRANSACTION_C2C_PANEL_CALCULATOR_HASH) {
    scrollToFinancingCalculatorPanel()
  }

  useMount(() => {
    if (hash === RESERVATION_HASHES.LOGIN) {
      navigate(pathname, {replace: true})
      const needsLoginOrVerification = !state.userData || state.userData.isEmailConfirmed === false
      if (needsLoginOrVerification) {
        const {LOGIN_SOURCE_ID_RESERVATIONS} = config.get('login')
        const {VERIFICATION_REMINDER: STEP_VERIFICATION_REMINDER} = config.get('LOGIN_STEPS')
        triggerLoginModal({
          modalProps: {
            source: LOGIN_SOURCE_ID_RESERVATIONS,
            trackingOrigin: hitOriginReservation,
            onLogin: () => navigate(`${ROUTES.RESERVATION.PAYMENT}?adId=${ad.id}`),
            enableVerificationReminder: true,
            initialStep: state.userData?.isEmailConfirmed === false ? STEP_VERIFICATION_REMINDER : undefined
          }
        })
      }
    }
  })

  const handleScrollToB2cFinancingPanel = ({context, event}) => {
    const trackEventData = async () => {
      const eventData = await domain.get('get_event_tracking_data_use_case').execute({
        ad,
        context,
        event
      })
      analyticsTrack(eventData)
    }
    trackEventData()
    scrollToFinancingCalculatorPanel()
  }

  const handleScrollToC2cPanel = ({context, event}) => {
    const trackEventData = async () => {
      const eventData = await domain.get('get_event_tracking_data_use_case').execute({
        ad,
        context,
        event
      })

      analyticsTrack(eventData)
    }

    trackEventData()
    dispatch({
      type: reducerActions.SHOW_FINANCING_C2C_PANEL
    })
    scrollToFinancingCalculatorPanel()
  }

  const handleScrollToPriceIndicatorPanel = async () => {
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      context: 'priceIndicator',
      event: TRACKING_EVENT_NAMES.PRICE_INDICATOR_CLICKED
    })

    analyticsTrack(eventData)

    const hasPanelPriceIndicatorRef = Boolean(panelPriceIndicatorRef.current)
    hasPanelPriceIndicatorRef && panelPriceIndicatorRef.current.scrollIntoView({...scrollParams, behavior: 'smooth'})
  }

  const handleOnCloseFormContactModal = useCallback(() => {
    setIsShowContactFormModal(false)
  }, [])

  const {
    CONSENTS: {LAST_DETAILS_VIEWED: lastDetailsViewedRequiredConsents}
  } = config.get('COMMON')

  const hasConsentsForLastDetailsViewed = useUserConsents(lastDetailsViewedRequiredConsents)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRedirectTo = useCallback(url => navigate(url), [])
  const updateAdFavoriteState = ({useCaseParams, isFavorite}) => {
    const [{adId: adIdFromUsecase}] = useCaseParams
    const {id: adId} = ad

    if (adIdFromUsecase === adId) {
      dispatch({
        type: reducerActions.SET_IS_FAVORITE,
        payload: {isFavorite}
      })
    }
  }

  const handleRefreshFavoriteState = (fetchedFavoritesAds = []) => {
    const favoriteAdIds = new Set(fetchedFavoritesAds.map(ad => ad.id))
    const isFavorite = favoriteAdIds.has(Number(ad.id))

    dispatch({
      type: reducerActions.SET_IS_FAVORITE,
      payload: {isFavorite}
    })
  }

  useMount(() => {
    if (redirectTo || isPreview) return

    criteoTrack({
      event: criteoTrackPageEvents.ADS_DETAIL,
      eventData: {adId: ad.id}
    })
  })

  useMount(() => {
    if (redirectTo || isPreview) return

    domain.get('save_page_view_hit_tracking_use_case').execute({adId: ad.id})
  })

  useMount(() => {
    if (redirectTo || isPreview) return

    analyticsTrackPage(pageTrackingData)
  })

  useMount(() => {
    if (redirectTo || isPreview) return
    if (isRecommendedAdsFallbackValue) {
      analyticsTrack({
        event: 'Recommended Ads Fallback',
        data: {
          hit_origin: pageName,
          hit_information: 'true',
          ad_id: ad?.id
        }
      })
    }
  })

  useMount(() => {
    if (offerTypeId === OFFER_TYPE_SUBSCRIPTION) {
      trigger('csat_renting_detail')
    }
  })

  useMount(() => {
    const {unsubscribe} = domain.get('login_authentication_use_case').subscribe(({result}) => {
      if (!result) return

      const {userData} = result

      dispatch({
        type: reducerActions.SET_USER_DATA,
        payload: {
          userData
        }
      })
    })

    return unsubscribe
  })

  useMount(() => {
    const onNext = response => {
      const {
        params,
        result: {userId}
      } = response
      if (params.length) {
        const {email, name, phone, zipCode} = params[0]
        dispatch({
          type: reducerActions.SET_USER_DATA,
          payload: {
            userData: {email, name, phone, zipCode, userId}
          }
        })
      }
    }
    const onError = () => {}
    const _contactSeller$ = domain.get('contact_seller_use_case').$.execute.subscribe(onNext, onError)
    return () => _contactSeller$.dispose()
  })

  useMount(() => {
    const onNext = ({params}) => updateAdFavoriteState({useCaseParams: params, isFavorite: false})
    const onError = () => {}
    const _deleteUserFavorite$ = domain.get('delete_user_favorites_use_case').$.execute.subscribe(onNext, onError)

    return () => _deleteUserFavorite$.dispose()
  })

  useMount(() => {
    const onNext = async ({params}) => {
      updateAdFavoriteState({useCaseParams: params, isFavorite: true})
    }
    const onError = () => {}
    const _addUserFavorite$ = domain.get('add_user_favorites_use_case').$.execute.subscribe(onNext, onError)

    return () => _addUserFavorite$.dispose()
  })

  useEffect(() => {
    if (redirectTo) return
    const trackLastDetailsViewed = async () => {
      try {
        await domain.get('save_last_detail_viewed_ads_use_case').execute({adId: ad.id})

        const {lastDetailsViewed} = await domain.get('get_last_details_viewed_ads_use_case').execute()

        const lastDetailsViewedString =
          Array.isArray(lastDetailsViewed) && lastDetailsViewed.map(item => item.id).join(',')

        Boolean(lastDetailsViewedString) &&
          analyticsTrack({
            event: 'Last Details Seen',
            data: {
              ads: lastDetailsViewedString
            }
          })
      } catch (e) {
        console.error('Cannot retrieve last details viewed', e) // eslint-disable-line
      }
    }

    hasConsentsForLastDetailsViewed && trackLastDetailsViewed()
  }, [hasConsentsForLastDetailsViewed]) // eslint-disable-line

  const primaryCategories = [DEFAULT_CATEGORY1_ID, CARS_WITHOUT_LICENSE_CATEGORY1_ID]
  const isPrimaryCategory = primaryCategories.includes(category1Id)

  if (redirectTo) {
    navigate(redirectTo)
    return <></>
  }

  const {culture} = i18n
  const provinces = config.get('provinces')
  const {DEALER_PACKS} = config.get('dealer')

  const {AD_DETAIL_DEALER_ADS_LIST_TYPE: listTypeDealerAds, AD_DETAIL_RELATED_ADS_LIST_TYPE: listTypeRelatedAds} =
    config.get('tracking').TRACKING_TRANSLATIONS_BY_CULTURE[culture]
  const {AD_SUBSCRIPTION_PRICE_OPTION_CLICKED} = config.get('tracking').TRACKING_EVENT_NAMES
  const {HIT_ORIGINS} = config.get('tracking')
  const {IS_GOT_ENABLED: isGotEnabled} = config.get('login')
  const {IS_INSTANT_OFFER_FEATURE_ENABLED} = config.get('seller')
  const isWebview = config.get('isWebview')

  const {pack: dealerPackType} = seller.dealer
  const {services = [], mainRentingServicesIncluded: isMainServicesIncluded} = seller.dealer

  const {createAlertError} = pageErrors
  const {equipment = {}, specifications = {}} = vehicleInfo

  const {standard: standardEquipment = [], optional: optionalEquipment = []} = equipment

  const {
    acceleration,
    bodyType,
    electricFeatures,
    consumptionAverage,
    consumptionExtraUrban,
    consumptionUrban,
    cubicCapacity,
    doors,
    emissions,
    fuelTankCapacity,
    height,
    hp,
    length,
    luggageCapacity,
    powerElectric,
    seats,
    topSpeed,
    weight,
    width
  } = specifications

  const hasDealerPack = Boolean(dealerPackType)
  const hasManufacturerPrice = Boolean(specifications.manufacturerPrice)
  const isDealerWithPackAdvance = seller.isDealer && hasDealerPack && dealerPackType === DEALER_PACKS.ADVANCE
  const isDealerWithPackExpert = seller.isDealer && hasDealerPack && dealerPackType === DEALER_PACKS.EXPERT
  const isDealerSubscription = seller.isDealer && dealerPackType === DEALER_PACKS.SUBSCRIPTION

  const hasServices = Boolean(services.length)
  const hasTagclouds = Boolean(tagclouds.length)
  const hasRatings = Boolean(seller?.dealer?.ratings)

  // these services are not available for Subscription Ads nor new vehicles
  const isCarfaxAvailable = Boolean(AVAILABLE_SERVICE_PROVIDERS[CARFAX]) && !isSubscriptionAd && !isNewVehicle

  const showNews = Boolean(newsFeed && newsFeed.items.length)
  const legacyNewsFeed = showNews && {...newsFeed, items: newsFeed.items.slice(0, LEGACY_NEWS_ITEMS_COUNT)}
  const newsFeedUpdated = showNews && adaptDomainNewsFeed({domainConfig: config, newsFeed: legacyNewsFeed})
  const hasDealerAds = Boolean(dealerAds && dealerAds.items.length)
  const hasStandardEquipmentInfo = Boolean(
    vehicleInfo.equipment && vehicleInfo.equipment.standard && vehicleInfo.equipment.standard.length
  )
  const hasBreadcrumb = Boolean(breadcrumb.length) && !pageErrors.breadcrumbError

  const hasCertification = Boolean(
    vehicleCertification && vehicleCertification.features && vehicleCertification.features.length
  )
  const hasTechnicalDetailsUrl = Boolean(vehicleInfo.technicalDetailsUrl)

  const primaryOfferTypes = [OFFER_TYPE_SECOND_HAND, OFFER_TYPE_NEW, OFFER_TYPE_KM0, OFFER_TYPE_SUBSCRIPTION]

  const isPrimaryOfferType = primaryOfferTypes.includes(offerTypeId)

  const showSearchSave = (isPrimaryOfferType && isPrimaryCategory) || isSubscriptionAd

  const vehicleData = {
    bodyType,
    color: ad.color,
    cubicCapacity,
    doors,
    emissions,
    environmentalLabel: ad.environmentalLabel,
    fuelTypeId: ad.fuelTypeId,
    hasOfficialWarranty: ad.hasOfficialWarranty,
    hasWarranty: ad.hasWarranty,
    km: ad.km,
    hp,
    location: ad.location,
    powerElectric,
    province: ad.province,
    seats,
    transmissionTypeId: ad.transmissionTypeId,
    warrantyMonths: ad.warrantyMonths,
    warrantyText: ad.warrantyText,
    year: ad.year
  }

  const basicDataSheet = [
    {
      acceleration,
      electricFeatures,
      consumptionAverage,
      consumptionExtraUrban,
      consumptionUrban,
      fuelTankCapacity,
      height,
      length,
      luggageCapacity,
      topSpeed,
      weight,
      width
    }
  ]

  const vehicleEquipment = {
    basicDataSheet,
    standard: standardEquipment
  }

  const panelEquipmentCTA = hasTechnicalDetailsUrl && {
    design: panelEquipmentCTADesigns.OUTLINE,
    text: i18n.t('COMMON.SEE_FULL_DATASHEET'),
    type: panelEquipmentCTATypes.link,
    url: vehicleInfo.technicalDetailsUrl
  }

  const sectionStandardFeaturesDescription = i18n.t('SECTION.STANDARD_FEATURES.DESCRIPTION', {
    boldStart: '<b>',
    boldEnd: '</b>',
    price: hasManufacturerPrice && i18n.c(specifications.manufacturerPrice),
    year: vehicleInfo.year
  })

  const madeModelVersionText = [ad.make, ad.model, ad.version].join(' ')
  const adSpecificationsText = madeModelVersionText.trim() || ad.title

  const getSellerPhoneNumber = () => (seller.isPrivate ? seller.user.phone : seller.dealer.phoneNumber)
  const sellerPhone = getSellerPhoneNumber()

  const getProvinceById = province => province.id === ad.provinceId
  const getProvinceName = province => province.label
  const provinceName = ad.provinceId ? getProvinceName(provinces.find(getProvinceById)) : null

  const handleSubscriptionOptionSelected = ({selectedOption, context}) => {
    domain
      .get('get_event_tracking_data_use_case')
      .execute({
        event: AD_SUBSCRIPTION_PRICE_OPTION_CLICKED,
        context,
        ad
      })
      .then(subscriptionPriceOptionClickEvent => {
        analyticsTrack(subscriptionPriceOptionClickEvent)
      })

    domain
      .get('get_lead_data_tracking_use_case')
      .execute({
        ad,
        adType,
        dealerId: seller?.contractId,
        financingInstalmentPrice: financingData?.fee || undefined,
        filters: {provinces: multiProvinceTrackingData},
        subscriptionPriceOption: selectedOption
      })
      .then(([leadDataError, leadDataResponse]) => {
        dispatch({
          type: reducerActions.UPDATE_LEAD_DATA,
          payload: {
            leadData: leadDataResponse
          }
        })

        dispatch({
          type: reducerActions.SET_SUBSCRIPTION_PRICE_OPTION_SELECTED,
          payload: {
            subscriptionPriceOptionSelected: selectedOption,
            selectedTabAdInfo: TARGET_BUYER_TAB_INDEX[selectedOption.targetBuyer]
          }
        })
      })
  }

  const handleOnCalculatorChange = ({financingData, reset}) => {
    reset
      ? dispatch({
          type: reducerActions.RESET_CALCULATED_FINANCING_DATA
        })
      : dispatch({
          type: reducerActions.SET_CALCULATED_FINANCING_DATA,
          payload: {calculatedFinancingData: financingData}
        })
  }

  const handleClickStock = () =>
    analyticsTrack({
      event: 'Dealer Stock Clicked',
      data: {
        hit_origin: 'Detail More Vehicles Dealer Stock Clicked'
      }
    })

  const handleLeadPhoneCall = () =>
    dispatch({
      type: reducerActions.HANDLE_LEAD,
      payload: {
        showModal: true,
        contactType: listRelatedAdsContactTypes.phoneCall
      }
    })

  const handleCloseModal = () =>
    dispatch({
      type: reducerActions.SET_SHOW_MODAL,
      payload: {
        showModal: false
      }
    })

  const handleClickGoToRatings = async () => {
    if (!hasRatings) return

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RATINGS_CLICKED,
      origin: HIT_ORIGINS.RATING_DETAIL
    })

    eventData && analyticsTrack(eventData)
    setIsOpenModalDealerRating(true)
  }

  // eslint-disable-next-line no-nested-ternary
  const cardSeller = seller.isPrivate ? (
    <div className="mt-Detail-cardUser">
      <CardUser
        adId={ad.id}
        hasCriteoEvents
        hidePhone={ad.hidePhone}
        isLoggedUser={Boolean(state.userData)}
        leadData={state.leadData}
        multiprovince={multiProvinceTrackingData}
        pageName={pageName}
        user={seller.user}
      />
    </div>
  ) : (
    <HeroDealer
      adCategoryId={category1Id}
      adId={ad.id}
      dealer={seller.dealer}
      design={heroDealerDesigns.COMPRESSED}
      extendedLeadTrackingData={state.leadData}
      isFavorite={Boolean(dealerFavoriteInfo)}
      isHidePhone={ad.hidePhone}
      isLoggedUser={Boolean(state.userData)}
      isRentingDealer={isDealerSubscription}
      linkFactory={getLinkFactoryWithState({multiProvinceTrackingData})}
      multiProvinceTrackingData={multiProvinceTrackingData}
      onClickGoToRatings={handleClickGoToRatings}
      pageName={pageName}
      ratingsList={ratingsList}
      ref={panelInfoDealerRef}
      titleAsLink
    />
  )

  const showSectionServices = hasServices && !isDealerSubscription
  const showSectionServicesSubscription = hasServices && isDealerSubscription
  const showMoreSellerVehicles = (isDealerWithPackAdvance || isDealerWithPackExpert) && hasDealerAds

  // eslint-disable-next-line react/no-unstable-nested-components
  const PanelAdInfoComponent = ({ad}) => (
    <PanelAdInfo
      ad={{
        ...ad,
        financingData,
        ...(state.hasCalculatedFinancingData && {
          financingData: state.calculatedFinancingData
        }),
        isPrimaryCategory,
        modifiedDate: ad.publicationDate,
        publishedDate: ad.creationDate
      }}
      extendedTrackingData={extendedTrackingData}
      leadContactButtons={
        <StickyLeadContactButtons
          ad={ad}
          adId={ad.id}
          buttonWhatsapp={
            seller.dealer?.hasWhatsappProduct && (
              <Button
                type="button"
                color={atomButtonColors.SOCIAL_WHATSAPP}
                design={atomButtonDesigns.SOLID}
                shape={atomButtonShapes.CIRCULAR}
                onClick={handleStickyWhatsappIntent}
                size={atomButtonSizes.LARGE}
                leftIcon={<IconWhatsApp size="medium" />}
                fullWidth
              >
                {/* ff_web_lead_experience_whatsapp_label_buttons — icon-only CTA in the treated variant */}
                {hasPhoneCallLabel ? null : i18n.t('WHATSAPP_LEAD_BUTTON.LABEL')}
              </Button>
            )
          }
          contactFormRef={contactFormWrapperRef}
          contractId={seller.contractId}
          dealer={seller.dealer}
          financingData={financingData}
          financingVehicleFee={financingData?.fee}
          hasCriteoEvents
          hasFinancingData={hasFinancing}
          // ff_web_lead_experience_whatsapp_label_buttons
          hasPhoneCallLabel={hasPhoneCallLabel}
          hidePhone={ad.hidePhone}
          isLoggedUser={Boolean(state.userData)}
          isLoggedUsersPhoneCall={isLoggedUsersPhoneCall}
          showReservationMainCTA={showReservationMainCTA}
          showCanariasBannerInfo={showCanariasBannerInfo}
          leadData={state.leadData}
          multiprovince={multiProvinceTrackingData}
          offerTypeId={offerTypeId}
          onLeadPhoneCall={handleLeadPhoneCall}
          pageName={pageName}
          phoneNumber={sellerPhone}
          stickyRef={panelAdInfoRef}
        />
      }
      linkFactory={getLinkFactoryWithState({multiProvinceTrackingData})}
      seller={seller}
      hasFinancingData={hasFinancing}
      handleScrollToB2cFinancingPanel={handleScrollToB2cFinancingPanel}
      handleScrollToC2cPanel={handleScrollToC2cPanel}
      handleScrollToContactForm={handleScrollToContactForm}
      handleScrollToFinancingTerms={handleScrollToFinancingTerms}
      handleScrollToPriceIndicatorPanel={handleScrollToPriceIndicatorPanel}
      handleSubscriptionOptionSelected={handleSubscriptionOptionSelected}
      isFavorite={state.isFavorite}
      subscriptionPriceOptionSelected={state.subscriptionPriceOptionSelected}
      pageName={pageName}
    />
  )

  const hasBusinessAndPrivateTargetBuyer = Boolean(ad.subscriptionBusinessAdInfo)
  const showTargetBuyerOptions = isRenting && hasBusinessAndPrivateTargetBuyer

  const handleTargetBuyerTabChange = (ev, {numTab}) => {
    const _ad = numTab === TARGET_BUYER_TAB_INDEX.BUSINESS ? ad.subscriptionBusinessAdInfo : ad
    const optionSelected =
      _ad.subscriptionPriceOptions.find(
        option =>
          option.totalMonths === state.subscriptionPriceOptionSelected?.totalMonths &&
          option.kmIncluded === state.subscriptionPriceOptionSelected?.kmIncluded &&
          option.kmIncludedPeriod === state.subscriptionPriceOptionSelected?.kmIncludedPeriod
      ) || _ad.subscriptionPriceOptions.find(option => option.monthlyPrice === _ad.monthlySubscriptionFee)
    handleSubscriptionOptionSelected({selectedOption: optionSelected, context: 'commitment'})
  }

  const handleOnFieldChange = (field, value) => {
    if (field === FORM_FIELDS.TARGET_BUYER.NAME) {
      const numTab = TARGET_BUYER_TAB_INDEX[value]
      handleTargetBuyerTabChange(null, {numTab})
    }
  }

  const onCreateAlert = async () => {
    return domain
      .get('create_alert_user_alert_use_case')
      .execute({search: alertSearch, adId: ad.id})
      .then(createAlertResponse => {
        setCreatedAlertState(createAlertResponse)
      })
      .catch(error => {
        console.warn(error)
      })
  }

  const alertCreatedOrigin =
    createdAlertState && createdAlertState !== createdAlert
      ? 'Ad Replied Email Alert Created'
      : searchSaveTrackingOrigins.AD_DETAIL

  const {SMARTBANNER_SOURCE_IDS} = config.get('smartbanner')

  return (
    <ToastProvider toastManager={favoriteLeadToastManager}>
      <Layout.Basic linkFactory={LinkFactory} isHeaderless={false} isHeaderFlush isPaddingless isMarginless>
        <CsatNpsTracker adId={ad.id} isDisabled={isPreview || !ad.id || Boolean(redirectTo)} />
        <NavigationAdToolbar
          ad={ad}
          adMonthlyFee={state.subscriptionPriceOptionSelected?.monthlyPrice || ad.monthlySubscriptionFee}
          contractId={seller.contractId}
          defaultLinks={breadcrumb}
          extendedTrackingData={extendedTrackingData}
          hasDiscardButton={!isRenting}
          isFavorite={state.isFavorite}
          isFavoriteLeadEnabled={isFavoriteLeadEnabled}
          isSubscriptionAd={isSubscriptionAd}
          linkFactory={getLinkFactoryWithState({multiProvinceTrackingData})}
          onFavoriteLeadConfirm={handleFavoriteLeadConfirm}
        />
        <main className="mt-Detail">
          <div className="mt-Detail-mainSection">
            <div className="mt-Detail-inner mt-Detail-infoWrapper">
              <div className="mt-Detail-galleryWrapper">
                <Gallery ad={ad} adType={adType} videoPlay={videoPlay} videoTime={videoTime} />
              </div>

              {showCanariasBannerInfo && (
                <div className="mt-Detail-bannerInfoWrapper">
                  <BannerInfo rounded />
                </div>
              )}

              <div className={panelAdInfoWrapperClassName} ref={panelAdInfoRef}>
                {showTargetBuyerOptions ? (
                  <PanelBasic isPaddingless rounded={showCanariasBannerInfo ? 'none' : null}>
                    <MoleculeTabs
                      autoScrollIntoView={false}
                      onChange={handleTargetBuyerTabChange}
                      variant={moleculeTabsVariants.HIGHLIGHTED}
                      activeTabIndex={state.selectedTabAdInfo}
                    >
                      <MoleculeTab label={i18n.t('COMMON.RENTING_PRIVATE')}>
                        <div className="mt-Detail-panelTabContent" key="renting-private">
                          <PanelAdInfoComponent ad={ad} />
                        </div>
                      </MoleculeTab>
                      <MoleculeTab label={i18n.t('COMMON.RENTING_BUSINESS')}>
                        <div className="mt-Detail-panelTabContent" key="renting-business">
                          <PanelAdInfoComponent ad={adRentingForBusiness} />
                        </div>
                      </MoleculeTab>
                    </MoleculeTabs>
                  </PanelBasic>
                ) : (
                  <PanelBasic rounded={showCanariasBannerInfo ? 'none' : null}>
                    <PanelAdInfoComponent ad={ad} />
                  </PanelBasic>
                )}
              </div>
              <SharedSmartbanner
                pageName={pageName}
                position={sharedSmartbannerPositions.STICKY}
                hasAdToolbarOffset={true}
                source={SMARTBANNER_SOURCE_IDS.ADS_DETAIL}
                trackingOrigin={hitOriginSmartbannerAdDetail}
                trackingInformation={hitInformationSmartbannerPositionTop}
              />
              {showSectionServicesSubscription && (
                <SectionServices
                  services={services}
                  isMainServicesIncluded={isMainServicesIncluded}
                  context={sectionServicesContext.DETAIL_SUBSCRIPTION}
                />
              )}
              {/* temporarily hide this for Subscription Ads until BE resolves the issue with this feature */}
              {showSearchSave && (
                <PanelSearchSave
                  adId={ad.id}
                  createdAlert={createdAlertState}
                  province={provinceName}
                  search={alertSearch}
                  searchSaveTrackingOrigin={alertCreatedOrigin}
                  vehicle={adSpecificationsText}
                />
              )}
              <PanelAdDetails
                ad={ad}
                contractId={seller.contractId}
                extendedTrackingData={extendedTrackingData}
                isFavorite={state.isFavorite}
                isPreview={isPreview}
                isSubscription={isSubscriptionAd}
                linkFactory={getLinkFactoryWithState({multiProvinceTrackingData})}
                middleContent={
                  showPanelVehicleReservation ? (
                    <PanelVehicleReservation
                      ad={ad}
                      financingData={financingData}
                      seller={seller}
                      reservationNewFlow={reservationNewFlow}
                      openReservationFaq={hash.startsWith(RESERVATION_HASHES.FAQ)}
                      openReservationDrawer={hash.startsWith(RESERVATION_HASHES.OPEN_DRAWER)}
                    />
                  ) : null
                }
                isCanarias={isCanarias}
                optionalEquipment={optionalEquipment}
                vehicleData={vehicleData}
                pageName={pageName}
                onOpenNet={onOpenNet}
              />
              {showPriceIndicator && (
                <PanelPriceIndicator
                  average={ad.priceAverageIndicator}
                  offerTypeId={offerTypeId}
                  price={ad.price}
                  rank={ad.priceRankIndicator}
                  ref={panelPriceIndicatorRef}
                />
              )}
              {(showC2cFinancing || showB2cFinancingPanel || showSimplifiedFinancingPanel) && (
                <div id={TRANSACTION_C2C_PANEL_ID}>
                  <DetailPanelFinancingLazy
                    ad={ad}
                    seller={seller}
                    baseFinancingData={financingData}
                    showC2cFinancing={showC2cFinancing}
                    showB2cFinancingPanel={showB2cFinancingPanel}
                    id={TRANSACTION_C2C_PANEL_CALCULATOR_ID}
                    onCalculatorChange={handleOnCalculatorChange}
                    onContactSellerCtaClick={handleScrollToContactSellerWithFinancing}
                    vehicleInfo={vehicleInfo}
                  />
                </div>
              )}
              {Boolean(seller.isPrivate) && showFinancingLendoCard && (
                <CardFinancingLendo adId={ad.id} pageName={pageName} price={ad.price} />
              )}
              {!isSubscriptionAd && <AdvertisingTextlinks pageName={pageName} />}
              {isCarfaxAvailable && hasVehicleHistory && (
                <CardCarfax adId={ad.id} adTitle={ad.title} pageName={pageName} vehicleHistory={vehicleHistory} />
              )}
              {hasCertification && <CardCertification certification={vehicleCertification} />}
              {IS_INSTANT_OFFER_FEATURE_ENABLED && ad.isProfessional && !isSubscriptionAd ? (
                <SectionDetailInstantOffer />
              ) : null}
              <SectionBasic title={i18n.t('SELLER.INFO_TITLE')}>{cardSeller}</SectionBasic>
              {showSectionServices && <SectionServices services={services} />}
              {showMoreSellerVehicles && (
                <div className="mt-Detail-relatedAds" ref={outerRef}>
                  {isNear && (
                    <TrackingListRelatedAds
                      ads={dealerAds.items}
                      listType={listTypeDealerAds}
                      onProductsReceived={setDealerAdsProducts}
                    />
                  )}
                  <ListRelatedAds
                    ads={dealerAds.items}
                    description={i18n.t('SELLER.VEHICLES_COUNT', dealerAds.totalResults)}
                    display={
                      isHigherThanMobileResolution ? listRelatedAdsDisplay.grid : listRelatedAdsDisplay.scrolling
                    }
                    itemsPerRow={4}
                    link={
                      <Button
                        design={atomButtonDesigns.LINK}
                        href={seller.dealer.stockPageUrl}
                        link
                        onClick={handleClickStock}
                      >
                        {i18n.t('COMMON.ALL')}
                      </Button>
                    }
                    title={i18n.t('COMMON.MORE_SELLER_VEHICLES')}
                    extendedTrackingData={dealerAdsProducts}
                    carouselType={listTypeDealerAds}
                    pageType="detail"
                    pageNameOrigin="detail"
                    listType={listTypeDealerAds}
                  />
                </div>
              )}
              {hasStandardEquipmentInfo && (
                <SectionBasic
                  title={i18n.t('COMMON.EQUIPMENT.MAIN_SPECIFICATIONS')}
                  description={sectionStandardFeaturesDescription}
                >
                  <PanelEquipment cta={panelEquipmentCTA} equipment={vehicleEquipment} />
                </SectionBasic>
              )}
              {hasRelatedAds && (
                <div ref={outerRef}>
                  <SectionBasic>
                    {isNear && (
                      <TrackingListRelatedAds
                        ads={relatedAds}
                        listType={listTypeRelatedAds}
                        onProductsReceived={setRelatedAdsProducts}
                      />
                    )}
                    <ListRelatedAds
                      title={i18n.t('COMMON.SIMILAR_ADS')}
                      ads={relatedAds}
                      itemsPerRow={4}
                      display={listRelatedAdsDisplay.slides}
                      numberOfSlides={isDesktop ? 4 : 2}
                      maxItems={10}
                      extendedTrackingData={relatedAdsProducts}
                      pageNameOrigin="detail"
                      carouselType="carrousel listado coches anuncios recomendados"
                      pageType="detail"
                    />
                  </SectionBasic>
                </div>
              )}
              {showNews && (
                <SectionBasic title={i18n.t('COMMON.NEWS_ABOUT')}>
                  <FeedArticles
                    initialNewsSearchFeed={newsFeedUpdated}
                    linkFactory={LinkFactory}
                    showInlineAd={false}
                  />
                </SectionBasic>
              )}
              {NEWSLETTER_BANNER_ENABLED && (
                <SectionBasic>
                  <NewsletterSubscription pageName={pageName} />
                </SectionBasic>
              )}
              {(hasFinancing || showC2cFinancing) && isHigherThanMobileResolution && (
                <div className="mt-Detail-financingTerms" id={FINANCING_TERMS_CONDITIONS_ID}>
                  <SectionFinancingTerms
                    financingData={getFinancingData({
                      ad,
                      financingData,
                      state
                    })}
                  />
                </div>
              )}
            </div>
            <aside className="mt-Detail-inner mt-Detail-aside">
              <DetailFormContactSellerWrapper
                ad={ad}
                adSpecificationsText={adSpecificationsText}
                adType={adType}
                alertSearch={alertSearch}
                contactFormWrapperRef={contactFormWrapperRef}
                contactType={state.contactType}
                dealer={seller.dealer}
                handleOnFieldChange={handleOnFieldChange}
                handleScrollToFinancingTerms={handleScrollToFinancingTerms}
                hasFinancing={hasFinancing}
                handleScrollToB2cFinancingPanel={handleScrollToB2cFinancingPanel}
                hidePhone={ad.hidePhone}
                initialLeadEnrichmentValue={state.initialLeadEnrichmentValue}
                isLoggedUsersPhoneCall={isLoggedUsersPhoneCall}
                isSubscriptionLead={isSubscriptionAd}
                leadData={state.leadData}
                onCloseModal={handleCloseModal}
                onRedirectTo={handleRedirectTo}
                pageName={pageName}
                provinceName={provinceName}
                relatedAds={relatedAds}
                seller={seller}
                sellerPhone={sellerPhone}
                showReservationMainCTA={showReservationMainCTA}
                showSearchSave={showSearchSave}
                showModal={state.showModal}
                showTargetBuyerOptions={showTargetBuyerOptions}
                targetBuyer={state.targetBuyer}
                userData={state.userData}
                vehicleInfo={vehicleInfo}
                multiprovince={multiProvinceTrackingData}
                enableAlertCreation
                onCreateAlert={onCreateAlert}
                enableLeadEnrichment={enableLeadEnrichment}
                isAdReservable={showPanelVehicleReservation}
                subscriptionPriceOptionSelected={state.subscriptionPriceOptionSelected}
                // ff_web_lead_experience_whatsapp_label_buttons
                whatsappLeadExperienceVariant={whatsappLeadExperienceVariant}
              />
            </aside>
          </div>
          {(hasFinancing || showC2cFinancing) && !isHigherThanMobileResolution && (
            <div className="mt-Detail-financingTermsWrapper" id={FINANCING_TERMS_CONDITIONS_ID}>
              <SectionFinancingTerms
                financingData={{
                  ...financingData,
                  ...(state.hasCalculatedFinancingData && {
                    financingData: state.calculatedFinancingData
                  })
                }}
              />
            </div>
          )}
          {hasTagclouds &&
            tagclouds.map((tagcloud, index) => (
              <MotorTagCloud
                initialTagclouds={tagcloud}
                key={`${ad.makeId}-${index}`}
                linkFactory={LinkFactory}
                makeId={ad.makeId}
                modelId={ad.modelId}
                pageName={pageName}
              />
            ))}
          {hasBreadcrumb && <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />}

          <NetDrawer
            pageName={pageName}
            isOpen={isNetOpen}
            onClose={onCloseNet}
            onOpen={onOpenNet}
            initialSearchValue={netSearchValue}
            initialSuggestions={netInitialSuggestions}
            context={{page: {type: NET_SUGGESTION_TYPE.AD_DETAIL, adId: ad.id}}}
            headerText={i18n.t('NET.WELCOME.TEXT_AD_DETAIL')}
            onFavoriteChange={handleRefreshFavoriteState}
          />
        </main>
        <NotificationsAdFavorite isAddSuccessHidden={isFavoriteLeadEnabled} />
        <NotificationsDealerFavorite />
        {showReservationWrongUserNotification && (
          <NotificationsVehicleReservation
            description={i18n.t('VEHICLE_RESERVATION.WRONG_USER_NOTIFICATION')}
            onClose={() => setShowReservationWrongUserNotification(false)}
          />
        )}
        {showReservationNotAvailableNotification && (
          <NotificationsVehicleReservation
            description={i18n.t('VEHICLE_RESERVATION.NOT_AVAILABLE_NOTIFICATION_DESCRIPTION')}
            onClose={() => setShowReservationNotAvailableNotification(false)}
            title={i18n.t('VEHICLE_RESERVATION.NOT_AVAILABLE_NOTIFICATION_TITLE')}
          />
        )}
        <Helmet {...helmet.head} />
        {helmet.scripts?.map((structuredData, index) => {
          return <ScriptJsonLD json={structuredData} key={index} />
        })}
        {isGotEnabled && !isWebview && <LoginGoogleOneTapToGigyaConnector />}
        {createAlertError && <SearchSave.ErrorNotification show={createAlertError} />}
        <AdDisplay pageName={pageName} ad={ad} adType={adType} seller={seller} />
        {isOpenModalDealerRating && (
          <ModalDealerRatings
            ad={ad}
            dealerId={seller?.dealer?.id}
            isLoading={isRatingsLoading}
            isOpenModalDealerRating={isOpenModalDealerRating}
            onCloseOpenModalDealerRating={setIsOpenModalDealerRating}
            pageName={pageName}
            ratingsList={ratingsList}
            ratingsSummary={seller?.dealer?.ratings}
          />
        )}
        {isShowContactFormModal && (
          <Suspense fallback={<></>}>
            <ModalBasic
              ariaLabelledby="Modal-title"
              closeOnOutsideClick
              header={
                <TitleBasic
                  as={titleBasicTags.h2}
                  size={titleBasicSizes.s}
                  title={i18n.t('FORM.CONTACT_SELLER_LABEL')}
                />
              }
              isInsetScrollArea
              isOpen
              onClose={handleOnCloseFormContactModal}
            >
              <div className="mt-Detail-contactFormModal">
                <DetailFormContactSellerWrapper
                  ad={ad}
                  adSpecificationsText={adSpecificationsText}
                  adType={adType}
                  alertSearch={alertSearch}
                  contactFormWrapperRef={contactFormWrapperRef}
                  contactType={state.contactType}
                  handleOnFieldChange={handleOnFieldChange}
                  handleScrollToFinancingTerms={handleScrollToFinancingTerms}
                  hasFinancing={hasFinancing}
                  handleScrollToB2cFinancingPanel={handleScrollToB2cFinancingPanel}
                  hidePhone={ad.hidePhone}
                  isLoggedUsersPhoneCall={isLoggedUsersPhoneCall}
                  isSubscriptionLead={isSubscriptionAd}
                  leadData={state.leadData}
                  onCloseModal={handleCloseModal}
                  onRedirectTo={handleRedirectTo}
                  pageName={pageName}
                  provinceName={provinceName}
                  relatedAds={relatedAds}
                  seller={seller}
                  sellerPhone={sellerPhone}
                  showSearchSave={showSearchSave}
                  showModal={state.showModal}
                  showTargetBuyerOptions={showTargetBuyerOptions}
                  targetBuyer={state.targetBuyer}
                  userData={state.userData}
                  vehicleInfo={vehicleInfo}
                  multiprovince={multiProvinceTrackingData}
                  isAdReservable={showPanelVehicleReservation}
                  subscriptionPriceOptionSelected={state.subscriptionPriceOptionSelected}
                  // ff_web_lead_experience_whatsapp_label_buttons
                  whatsappLeadExperienceVariant={whatsappLeadExperienceVariant}
                />
              </div>
            </ModalBasic>
          </Suspense>
        )}
        {isWhatsappLoading && (
          <AtomSpinner type={atomSpinnerTypes.FULL} overlayType={atomSpinnerOverlayTypes.DARK}>
            <CustomChildren />
          </AtomSpinner>
        )}
        {isWhatsappError && (
          <MoleculeNotification
            autoClose={moleculeNotificationAutoClose.manual}
            icon={<IconError size={ATOM_ICON_SIZES.medium} />}
            onClose={() => setIsWhatsappError(false)}
            position={moleculeNotificationPosition.top}
            show={isWhatsappError}
            showCloseButton
            showLeftIconMobile
            type={moleculeNotificationTypes.error}
            variation={moleculeNotificationVariations.negative}
          >
            <div className="mt-Detail-whatsappErrorNotification-content">
              <span className="mt-Detail-whatsappErrorNotification-title">{i18n.t('WHATSAPP_LEAD_ERROR.TITLE')}</span>
              <p className="mt-Detail-whatsappErrorNotification-body">{i18n.t('WHATSAPP_LEAD_ERROR.BODY')}</p>
            </div>
          </MoleculeNotification>
        )}
        {isFavoriteLeadEnabled && <ToastViewport />}
      </Layout.Basic>
    </ToastProvider>
  )
}

Detail.displayName = 'DetailLegacy'

export default Detail
